import React, { useState, useEffect } from "react";
import MaterialTable from "material-table";
import { Box } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import Popup from "../../../Base/views/Popup";
import { ConvertDate, DateConvertInDbFormat, getUserData, getUserId, getEmployeeId } from "../../../Base/helper/baseFunctions";
import { GetYetToCheckInDataHelper } from "../../helper/Team/TeamHelper";

const YetToCheckInPopup = ({
  openPopup,
  setOpenPopup,
  data = null,
  fromDate = null,
  toDate = null,
  isKpi = false,
  selectedDate = null,
  filters = {},
  dashboardFilters = null,
  businessUnitValue = [],
  shiftValue = [],
  employmentTypeValue = [],
  empNoValue = [],
  domain_name = null,
  user_id = null,
  reporting_manager_emp_id = null,
}) => {
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);

  const effectiveFromDate = isKpi
    ? DateConvertInDbFormat()
    : fromDate
    ? typeof fromDate === "string"
      ? fromDate.slice(0, 10)
      : DateConvertInDbFormat(fromDate)
    : selectedDate
    ? typeof selectedDate === "string"
      ? selectedDate.slice(0, 10)
      : DateConvertInDbFormat(selectedDate)
    : DateConvertInDbFormat();

  const effectiveToDate = isKpi
    ? DateConvertInDbFormat()
    : toDate
    ? typeof toDate === "string"
      ? toDate.slice(0, 10)
      : DateConvertInDbFormat(toDate)
    : effectiveFromDate;

  useEffect(() => {
    if (openPopup) {
      if (Array.isArray(data) && data.length > 0) {
        setListData(
          data.map((row) => ({
            ...row,
            business_unit: row?.business_unit || "N/A",
            shift_name: row?.shift_name || "N/A",
            employment_type: row?.employment_type || "N/A",
          }))
        );
      } else {
        setLoading(true);
        const effectiveReportingManager = reporting_manager_emp_id;

        let ats_loc = null;
        let sIds = null;
        let empTypeIds = null;
        let emp_id = null;
        let bUnit = null;
        let eNos = null;

        if (isKpi && dashboardFilters) {
          if (Array.isArray(dashboardFilters.location) && dashboardFilters.location.length > 0) {
            const locList = dashboardFilters.location
              .map((item) => {
                const locId = typeof item === "object" && item !== null ? item.work_location_id : item;
                return locId ? { ats_work_location_id: String(locId) } : null;
              })
              .filter(Boolean);
            if (locList.length > 0) {
              ats_loc = JSON.stringify(locList);
            }
          }

          if (Array.isArray(dashboardFilters.shift) && dashboardFilters.shift.length > 0) {
            const ids = dashboardFilters.shift
              .map((s) => (typeof s === "object" && s !== null ? s.shift_roster_configuration_id : s))
              .filter(Boolean);
            if (ids.length > 0) {
              sIds = `{${ids.join(",")}}`;
            }
          }

          if (Array.isArray(dashboardFilters.employmentType) && dashboardFilters.employmentType.length > 0) {
            const ids = dashboardFilters.employmentType
              .map((e) => (typeof e === "object" && e !== null ? e.employmenttype_id : e))
              .filter(Boolean);
            if (ids.length > 0) {
              empTypeIds = `{${ids.join(",")}}`;
            }
          }
        } else {
          const empList = (filters?.employee_id || []).map((e) => e?.employee_id || e);
          emp_id = empList.length > 0 ? empList : null;
          ats_loc = filters?.ats_work_location_id?.length > 0 ? filters?.ats_work_location_id : null;
          bUnit = businessUnitValue?.length > 0 ? businessUnitValue : null;
          sIds = shiftValue?.length > 0 ? shiftValue : null;
          empTypeIds = employmentTypeValue?.length > 0 ? employmentTypeValue : null;
          eNos = empNoValue?.length > 0 ? empNoValue : null;
        }

        GetYetToCheckInDataHelper({
          domain_name: domain_name,
          from_date: effectiveFromDate,
          to_date: effectiveToDate,
          emp_id: emp_id,
          reporting_manager_emp_id: effectiveReportingManager,
          ats_work_location_json: ats_loc,
          user_id: user_id || null,
          business_unit: bUnit,
          employee_no: eNos,
          shift_ids: sIds,
          employment_type_ids: empTypeIds,
        })
          .then((res) => {
            if (!res.hasError && Array.isArray(res.data?.data)) {
              const formatted = res.data.data.map((row) => ({
                ...row,
                business_unit: row?.business_unit || "N/A",
                shift_name: row?.shift_name || "N/A",
                employment_type: row?.employment_type || "N/A",
              }));
              setListData(formatted);
            } else {
              setListData([]);
            }
          })
          .catch((err) => {
            console.error("Error loading yet to check-in data:", err);
            setListData([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [
    openPopup,
    data,
    effectiveFromDate,
    effectiveToDate,
    isKpi,
    dashboardFilters,
    filters,
    businessUnitValue,
    shiftValue,
    employmentTypeValue,
    empNoValue,
    domain_name,
    user_id,
    reporting_manager_emp_id,
  ]);

  const handleExport = () => {
    try {
      const headers = [
        { label: "Emp No", key: "employee_no" },
        { label: "Name", key: "employee_name" },
        { label: "Business Unit", key: "business_unit" },
        { label: "Shift Time", key: "shift_name" },
        { label: "Employment Type", key: "employment_type" },
        { label: "Work Location", key: "work_location_name" },
      ];

      const fromDateStr = isKpi
        ? ConvertDate(DateConvertInDbFormat())
        : effectiveFromDate
        ? ConvertDate(effectiveFromDate)
        : ConvertDate(DateConvertInDbFormat());

      const toDateStr = isKpi
        ? ConvertDate(DateConvertInDbFormat())
        : effectiveToDate
        ? ConvertDate(effectiveToDate)
        : fromDateStr;

      let reportPeriod = "Yet to Check-In Employees";
      if (fromDateStr && toDateStr) {
        reportPeriod =
          fromDateStr === toDateStr
            ? `Yet to Check-In Employees - Date: ${fromDateStr}`
            : `Yet to Check-In Employees - Period: ${fromDateStr} to ${toDateStr}`;
      } else if (fromDateStr) {
        reportPeriod = `Yet to Check-In Employees - Date: ${fromDateStr}`;
      }

      const csvData = [[`"${reportPeriod.replace(/"/g, '""')}"`], [], headers.map((h) => `"${h.label}"`)];

      (listData || []).forEach((row) => {
        const rowData = headers.map((h) => {
          const value = row[h.key];
          if (value === null || value === undefined) return '""';
          if (typeof value === "string") {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return `"${value}"`;
        });
        csvData.push(rowData);
      });

      const csv = csvData.map((row) => row.join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `${reportPeriod.replace(/[/\\?%*:|"<>]/g, "-")}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
    }
  };

  const columns = [
    {
      title: "Emp No",
      field: "employee_no",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Name",
      field: "employee_name",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Business Unit",
      field: "business_unit",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Shift Time",
      field: "shift_name",
      editable: "never",
      width: "150px",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
    {
      title: "Employment Type",
      field: "employment_type",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
      render: (rowData) => rowData?.employment_type || "N/A",
    },
    {
      title: "Work Location",
      field: "work_location_name",
      editable: "never",
      cellStyle: { padding: "10px", textAlign: "left" },
      headerStyle: { padding: "10px", textAlign: "left" },
    },
  ];

  const fromDateFormatted = isKpi
    ? ConvertDate(DateConvertInDbFormat())
    : effectiveFromDate
    ? ConvertDate(effectiveFromDate)
    : ConvertDate(DateConvertInDbFormat());

  const toDateFormatted = isKpi
    ? ConvertDate(DateConvertInDbFormat())
    : effectiveToDate
    ? ConvertDate(effectiveToDate)
    : fromDateFormatted;

  const dateLabel =
    fromDateFormatted === toDateFormatted
      ? `Selected date : ${fromDateFormatted}`
      : `Selected date from ${fromDateFormatted} to ${toDateFormatted}`;

  return (
    <Popup
      fullWidth={true}
      maxWidth="md"
      title="Yet to Check-In"
      openPopup={openPopup}
      setOpenPopup={setOpenPopup}
      dialogContentSx={{ padding: "0px 10px 10px 10px" }}
    >
      <Box sx={{ width: "100%", pt: 1 }}>
        <MaterialTable
          title={<Box sx={{ fontSize: "13.5px", fontWeight: 600, color: "#555" }}>{dateLabel}</Box>}
          columns={columns}
          data={listData}
          isLoading={loading}
          actions={
            listData?.length > 0
              ? [
                  {
                    icon: () => <CloudDownloadIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />,
                    tooltip: "Export CSV",
                    isFreeAction: true,
                    onClick: handleExport,
                  },
                ]
              : []
          }
          options={{
            maxBodyHeight: "400px",
            minBodyHeight: "200px",
            rowStyle: { fontSize: "13px" },
            headerStyle: { padding: "10px 10px 10px 13px", fontWeight: "bold" },
            sorting: true,
            search: true,
            searchFieldAlignment: "right",
            searchAutoFocus: false,
            searchFieldVariant: "standard",
            paging: true,
            pageSizeOptions: [10, 25, 50],
            pageSize: 10,
            paginationType: "stepped",
            showFirstLastPageButtons: false,
            paginationPosition: "bottom",
            exportButton: false,
            columnsButton: false,
            showTitle: true,
          }}
          localization={{
            toolbar: {
              searchPlaceholder: "Search",
            },
          }}
        />
      </Box>
    </Popup>
  );
};

export default YetToCheckInPopup;
